import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, FilePdf, ArrowClockwise, Warning, DownloadSimple } from '@phosphor-icons/react';
import { dmsApi } from './dmsApi';
import { OfficeRender } from './OfficeRender';

const fmtMB = (b) => `${(Number(b || 0) / 1048576).toFixed(1)} MB`;
const fmtSec = (ms) => `${Math.floor(Number(ms || 0) / 1000)}秒`;

/**
 * Word / Excel / PowerPoint を PDF に変換して表示する。
 * 変換はサーバー側で一度だけ走り、以後は保存済みPDFを使い回す。
 * dark=true で暗いビューア（DocViewer）向けの配色にする。
 */
export function OfficePreview({ docId, doc, dark = false, heightClass = 'h-full' }) {
  const [state, setState] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmedLarge, setConfirmedLarge] = useState(false);
  const urlRef = useRef(null);
  const pollRef = useRef(null);
  const aliveRef = useRef(true);

  const muted = dark ? 'text-zinc-500' : 'text-zinc-400';
  const body = dark ? 'text-zinc-300' : 'text-zinc-600';
  const panel = dark ? 'border-zinc-700 bg-zinc-800/40' : 'border-zinc-200 bg-white';
  const btn = dark
    ? 'bg-teal-600 hover:bg-teal-500 text-white'
    : 'bg-teal-700 hover:bg-teal-800 text-white';

  const loadPdf = useCallback(async () => {
    try {
      const { url } = await dmsApi.officePreviewPdfUrl(docId);
      if (!aliveRef.current) { URL.revokeObjectURL(url); return; }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPdfUrl(url);
    } catch { /* 409 = まだ変換されていない。状態表示にまかせる */ }
  }, [docId]);

  const refresh = useCallback(async () => {
    try {
      const s = await dmsApi.officePreviewState(docId);
      if (!aliveRef.current) return null;
      setState(s);
      return s;
    } catch {
      if (aliveRef.current) setState({ is_office: false, unavailable: true });
      return null;
    }
  }, [docId]);

  const start = useCallback(async (force = false) => {
    setBusy(true);
    try {
      const s = await dmsApi.officePreviewStart(docId, force);
      if (!aliveRef.current) return;
      setState(s);
      if (s.status === 'ready') loadPdf();
    } catch (e) {
      if (aliveRef.current) {
        setState((prev) => ({ ...(prev || {}), status: 'failed',
          error: e?.response?.data?.detail || '変換を開始できませんでした' }));
      }
    } finally { if (aliveRef.current) setBusy(false); }
  }, [docId, loadPdf]);

  // 初回: 状態を見て、小さいファイルなら自動で変換を開始する
  useEffect(() => {
    aliveRef.current = true;
    setPdfUrl(null);
    setConfirmedLarge(false);
    refresh().then((s) => {
      if (!s || !aliveRef.current) return;
      if (s.prefer_render) return;        // 表計算は PDF にしない
      if (s.status === 'ready') loadPdf();
      else if (s.is_office && s.converter_available && !s.is_large && s.status === 'none') start();
    });
    return () => {
      aliveRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [docId, refresh, loadPdf, start]);

  // 変換中は進捗を問い合わせる
  useEffect(() => {
    if (state?.status !== 'processing') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      const s = await refresh();
      if (s?.status === 'ready') loadPdf();
    }, 1500);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [state?.status, refresh, loadPdf]);

  // Excel は表のまま見せる。PDF に変換すると用紙設定に従って列が切れるため、
  // 印刷レイアウトは出さない（旧 .xls は表として読めないので従来どおり PDF）。
  if (state?.prefer_render) {
    return <OfficeRender docId={docId} doc={doc} dark={dark} heightClass={heightClass} />;
  }

  if (pdfUrl) {
    return (
      <div className={`w-full ${heightClass} flex flex-col`} data-testid="dms-office-preview-pdf">
        <iframe title="office-preview" src={pdfUrl} className="w-full flex-1 bg-white rounded" />
        <p className={`shrink-0 text-[10px] ${muted} text-center py-1`}>
          {doc?.current_filename} を PDF に変換して表示しています
          {state?.pages ? `（${state.pages}ページ）` : ''}
        </p>
      </div>
    );
  }

  // Office ではない / 変換手段が無い → 従来どおりの案内
  if (state && (!state.is_office || state.unavailable)) {
    return (
      <div className={`py-12 text-center ${muted}`} data-testid="dms-office-preview-unsupported">
        <FileText size={40} weight="duotone" className="mx-auto mb-2" />
        <p className="text-xs">{doc?.current_filename}（プレビュー非対応形式）</p>
      </div>
    );
  }
  if (state && !state.converter_available) {
    // LibreOffice が無い環境（本番）。中身の描画に切り替える。
    return <OfficeRender docId={docId} doc={doc} dark={dark} heightClass={heightClass} />;
  }

  const st = state?.status;

  if (st === 'processing') {
    return (
      <div className={`py-12 px-6 text-center ${body}`} data-testid="dms-office-preview-progress">
        <ArrowClockwise size={34} className="mx-auto mb-3 animate-spin text-teal-500" />
        <p className="text-xs font-semibold">PDF に変換しています…</p>
        <p className={`text-[11px] mt-1 ${muted}`}>
          {state?.is_large ? '大きなファイルのため時間がかかります。' : ''}
          このまま開いたままお待ちください{state?.elapsed_ms ? `（経過 ${fmtSec(state.elapsed_ms)}）` : ''}
        </p>
        <div className={`mt-3 mx-auto w-48 h-1 rounded-full overflow-hidden ${dark ? 'bg-zinc-700' : 'bg-zinc-200'}`}>
          <div className="h-full w-1/3 bg-teal-500 animate-[officebar_1.2s_ease-in-out_infinite]" />
        </div>
        <style>{'@keyframes officebar{0%{margin-left:-33%}100%{margin-left:100%}}'}</style>
      </div>
    );
  }

  if (st === 'failed') {
    // 変換に失敗しても、中身の描画なら出せることが多い
    return (
      <div className={`w-full ${heightClass} flex flex-col min-h-0`} data-testid="dms-office-preview-failed">
        <div className={`shrink-0 flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] ${dark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800 border-b border-amber-200'}`}>
          <Warning size={13} weight="duotone" />
          <span className="truncate">そのままの見た目で出せませんでした（{state?.error}）— 中身を表示しています</span>
          <button type="button" onClick={() => start(true)} disabled={busy}
            className="shrink-0 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
            data-testid="dms-office-preview-retry">もう一度</button>
        </div>
        <div className="flex-1 min-h-0">
          <OfficeRender docId={docId} doc={doc} dark={dark} heightClass="h-full" />
        </div>
      </div>
    );
  }

  // 未変換。大きいファイルは自動で始めず、時間がかかることを伝えて確認してもらう
  if (st === 'none' && state?.is_large && !confirmedLarge) {
    return (
      <div className={`py-10 px-6 text-center ${body}`} data-testid="dms-office-preview-confirm">
        <FilePdf size={34} weight="duotone" className={`mx-auto mb-2 ${muted}`} />
        <p className="text-xs font-semibold">{doc?.current_filename}</p>
        <p className={`text-[11px] mt-1 ${muted}`}>
          {fmtMB(state.source_size)} の大きなファイルです。全体を PDF に変換して表示できますが、
          しばらく時間がかかります。
        </p>
        <div className={`mt-3 rounded-lg border p-3 text-left inline-block ${panel}`}>
          <button type="button" onClick={() => { setConfirmedLarge(true); start(); }} disabled={busy}
            className={`px-3 py-1.5 rounded text-[11px] font-semibold ${btn} disabled:opacity-50`}
            data-testid="dms-office-preview-start">全体を変換して表示する</button>
          <button type="button" onClick={() => dmsApi.downloadFile(docId, null, doc?.current_filename)}
            className={`ml-2 px-3 py-1.5 rounded text-[11px] font-semibold border ${dark ? 'border-zinc-600 text-zinc-300' : 'border-zinc-300 text-zinc-600'}`}
            data-testid="dms-office-preview-dl2">
            <DownloadSimple size={12} className="inline mr-1" />ダウンロードして開く
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`py-12 text-center ${muted}`} data-testid="dms-office-preview-loading">
      <ArrowClockwise size={30} className="mx-auto mb-2 animate-spin" />
      <p className="text-[11px]">プレビューを準備しています…</p>
    </div>
  );
}

export default OfficePreview;
